import { isEmpty } from 'ui/lodash';
import { useEffect, useRef, useState } from 'react';
import styles from 'styles/shop/catalogs.module.css';
import PropTypes from 'prop-types';
import { useQueryParams } from 'hooks/location';
import Chip from 'components/shop/Chip';
import { useInfiniteCatalog } from 'hooks/product';
import Filters from 'components/shared/filters/Filters';
import NoOrders from 'components/home/NoOrders';
import InfiniteScroller from 'react-infinite-scroller';
import { useSyncShop, useThemeName } from 'hooks/shop';
import { Container } from 'components/layout/Container';
import { BorderBottom } from 'components/layout/BorderBottom';
import { getProductCard } from 'containers/themes/productCard';
import { Grid } from '@mui/material';
import { useLanguageLoader } from 'contexts/language';
import { TopBar } from 'components/store/TopBar';
import { LocalCartProvider } from 'providers/localCart';
import { safeJsonParse } from 'ui/lib/json';
import Meta from 'components/seo/Meta';
import { useTrackGa } from 'hooks/ga';

// const emptyShop = '/images/store/shop.svg';

export default function Catalog({
  shop,
  savedFeed
}) {
  const params = useQueryParams();
  const lang = useLanguageLoader();
  const hasCatalog = params.has('catalog');
  const hasCatalogs = params.has('catalogs');
  const catalog = params.get('catalog');
  const catalogString = params.get('catalogs');
  const { uiConfig: { themeConfig } = {} } = shop;

  useTrackGa(shop._id, 'catalogs', shop._id);

  const getAllCatalogs = () => {
    if (hasCatalog) {
      return shop?.catalogs?.filter(x => x.status === 'live' && x.caption !== 'all');
    }
    if (hasCatalogs) {
      return catalogString.split(',')
        .map(x => ({
          caption: x,
          label: x
        }));
    }
    return [];
  };

  const items = getAllCatalogs();

  const getSelected = () => {
    if (hasCatalog) {
      return [{
        value: catalog,
        label: catalog
      }];
    }
    if (hasCatalogs) {
      return catalogString.split(',')
        .map(x => ({
          value: x,
          label: x
        }));
    }
    return [];
  };

  // const icon = get(shop, 'icon', []);
  // const shopIcon = isEmpty(icon) ? emptyShop : icon[0] || emptyShop;
  const [selected, setSelected] = useState(getSelected());
  const allItems = items.map(x => ({
    label: x.caption,
    value: x.caption
  }));
  const scrollRef = useRef();

  useSyncShop(shop);

  const themeName = useThemeName(shop);

  const ProductCard = getProductCard(themeName === 'custom' ?
    (themeConfig?.productCard?.name || 'celosia') : themeName);

  const getFilters = () => {
    let filters = null;
    filters = !isEmpty(selected) ? { catalogs: { $in: selected.map(x => x.label) } } : {};
    return filters;
  };

  const sorts = safeJsonParse(params.get('sorts'), { createdAt: -1 });

  const payload = {
    sorts: sorts,
    filters: getFilters()
  };

  const [products, loadMore, hasMore] = useInfiniteCatalog(savedFeed);

  const noProducts = products.length === 0;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.pageLoaded = 0;
    }
    loadMore(shop?._id, 0, payload);
  }, [JSON.stringify(payload)]);

  const onSelectCatalog = (items)=>{
    const itemSet = new Set(items);
    setSelected(allItems.filter(x => itemSet.has(x.value)));
  }

  return (
    <div className={styles.profileSection}>
      <LocalCartProvider shop={shop}>
        <Meta shop={shop}/>
        <TopBar shop={shop} showTabs={false}/>
        <Container>
          <div className={styles.head}>{lang('store.words.catalogs')}</div>
          <Chip
            dataId="shop_profile_catalog"
            chipStyles={styles.alignTopBn}
            selected={selected}
            items={allItems}
            onSelect={setSelected}
            shopId={shop?.shopId}
          />
          {noProducts && (
            <div>
              <NoOrders title="No products"/>
            </div>
          )}
          {!noProducts && (
            <div className={styles.overflow}>
              <InfiniteScroller
                loadMore={(e) => loadMore(shop?._id, e, payload)}
                hasMore={hasMore}
                useWindow={true}
                ref={scrollRef}
                pageStart={0}
              >
                <Grid container spacing={2}>
                  {products.map((tile, i) => (
                    <ProductCard key={i} shop={shop} product={tile}/>
                  ))}
                </Grid>
              </InfiniteScroller>
            </div>
          )}
        </Container>
        <BorderBottom/>
        <Filters
          catalog={selected.map(x => x.value)}
          all={shop?.catalogs}
          setCatalog={onSelectCatalog}
        />
      </LocalCartProvider>
    </div>
  );
}

Catalog.defaultProps = {
  savedFeed: []
};

Catalog.propTypes = {
  shop: PropTypes.object.isRequired,
  savedFeed: PropTypes.array,
};
